import React, { useState, useEffect } from "react";
import {
  Modal,
  TextField,
  Typography,
  TextDescription,
  Select,
} from "@ann/web-components";
import { observer } from "mobx-react";
import { ModalBodyContent, ErrorMessage } from "./CreateDepartmentModal.styled";
import { useUsersForDepartments } from "../../../../context";
import { useShellModalLock } from "../../../../../../../hooks";

const CreateDepartmentModal = ({
  size = "medium",
  isOpen,
  onClose,
  onCreateDepartment,
  existingNames = [],
}) => {
  useShellModalLock(isOpen);
  const { adminOwnerOptions, loading: loadingUsers, fetchUsers } = useUsersForDepartments();
  const [departmentName, setDepartmentName] = useState("");
  const [description, setDescription] = useState("");
  const [adminOwner, setAdminOwner] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showDuplicateError, setShowDuplicateError] = useState(false);



  useEffect(() => {
    if (isOpen) {
      fetchUsers({ page_size: 50 });
    }
  }, [isOpen, fetchUsers]);

  const isDuplicateName =
    departmentName.trim().length > 0 &&
    existingNames.some(
      (name) =>
        name.toLowerCase().trim() === departmentName.toLowerCase().trim()
    );
  const isFormValid = departmentName.trim().length > 0 && adminOwner !== null;

  const handleSave = async () => {
    if (isDuplicateName) {
      setShowDuplicateError(true);
      return;
    }
    if (!isFormValid) return;

    try {
      setIsCreating(true);
      const departmentData = {
        name: departmentName,
        description,
        adminOwner,
        adminOwnerName: adminOwner?.label?.split(" - ")[0] || "",
      };
      await onCreateDepartment?.(departmentData);
      setDepartmentName("");
      setDescription("");
      setAdminOwner(null);
      setIsCreating(false);
      setShowDuplicateError(false);
    } catch (error) {
      console.error("Error creating department:", error);
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setDepartmentName("");
    setDescription("");
    setAdminOwner(null);
    setIsCreating(false);
    setShowDuplicateError(false);
    onClose();
  };

  const handleNameChange = (value) => {
    setDepartmentName(value);
    if (showDuplicateError) setShowDuplicateError(false);
  };

  const handleAdminOwnerChange = (value) => {
    const selectedOption = adminOwnerOptions.find(
      (option) => option.value === value
    );
    setAdminOwner(selectedOption || null);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width="900px" size={size}  >
      <Modal.Header>
        <Modal.Header.Title>Create New Department</Modal.Header.Title>
        <Modal.Header.Description>
          <Typography
            category="body"
            size="medium"
            weight="regular"
            color="body"
          >
            Set up a new department with initial configuration.
          </Typography>
        </Modal.Header.Description>
      </Modal.Header>
      <Modal.Body>
        <ModalBodyContent>
          <div>
            <TextField
              label="Department Name"
              size="small"
              placeholder="e.g., Sales & Business Development"
              value={departmentName}
              onChange={handleNameChange}
              required={true}
              error={showDuplicateError && isDuplicateName}
            />
            {showDuplicateError && isDuplicateName && (
              <ErrorMessage>
                A department with this name already exists
              </ErrorMessage>
            )}
          </div>

          <TextDescription
            height="148px"
            label="Description"
            value={description}
            onChange={(value) => setDescription(value)}
            placeholder="Describe the department's purpose and scope..."
            size="small"
          />

          <Select
            label="Select Owner"
            value={adminOwner ?? undefined}
            onChange={handleAdminOwnerChange}
            placeholder={loadingUsers ? "Loading users..." : "Select admin owner"}
            size="small"
            width="100%"
            disabled={loadingUsers}
          >
            <Select.Menu>
              {adminOwnerOptions.length > 0 ? (
                adminOwnerOptions.map((option) => (
                  <Select.Item
                    key={option.value}
                    label={option.label}
                    value={option.value}
                  >
                    {option.label}
                  </Select.Item>
                ))
              ) : (
                <Select.Item label="No users available" value="" disabled>
                  No users available
                </Select.Item>
              )}
            </Select.Menu>
          </Select>
        </ModalBodyContent>
      </Modal.Body>
      <Modal.Actions align="end">
        <Modal.Button
          label="Cancel"
          onClick={handleClose}
          type="secondary"
          disabled={isCreating}
        />
        <Modal.Button
          label={isCreating ? "Creating..." : "Create Department"}
          onClick={handleSave}
          disabled={!isFormValid || isCreating}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default observer(CreateDepartmentModal);
